import React from 'react';
import { Helmet } from 'react-helmet';

// Import hero background images — same ones used by homepage slider
import slide1DesktopImport from '../../../images/slider/slide1.webp';
import slide1MobileImport from '../../../images/slider/mobile_optimized_slide1.webp';
const slide1Desktop = typeof slide1DesktopImport === 'object' ? slide1DesktopImport.default : slide1DesktopImport;
const slide1Mobile = typeof slide1MobileImport === 'object' ? slide1MobileImport.default : slide1MobileImport;

/**
 * Full-screen hero with overlay, badge, h1, subtitle, CTAs, trust bubbles.
 *
 * Props:
 *   bgImage       - hero background image URL (defaults to site hero bg)
 *   bgAlt         - alt text for bg image
 *   badge         - top badge text (e.g. "Doorstep Service")
 *   title         - main heading (can include JSX/line breaks)
 *   priceTag      - highlighted price line (e.g. "Starting ₹1,999")
 *   subtitle      - description paragraph
 *   ctas          - array of { label, href, variant: 'primary'|'secondary' }
 *   trustItems    - array of { value, label } for trust bubbles
 *   compact       - boolean, uses shorter hero height
 *   isMobileBrowser - for responsive image selection
 */
const PremiumHero = ({
  bgImage,
  bgAlt = 'Ride N Repair — Doorstep Vehicle Service',
  badge,
  title,
  priceTag,
  subtitle,
  ctas = [],
  trustItems = [],
  compact = false,
  isMobileBrowser = false,
  showCarAnimation = false,
  vehicleImage,           // optional: brand-specific vehicle image (transparent ghost overlay)
  vehicleImageAlt = '',
}) => {
  // Use provided bgImage, or fall back to site hero images
  const heroImg = bgImage || (isMobileBrowser ? slide1Mobile : slide1Desktop);

  return (
    <section className={`rr-premium-hero${compact ? ' rr-premium-hero--compact' : ''}`}>
      {/* Preload the LCP hero image into <head> so the browser fetches it
          immediately (before the body <img> is parsed). Same image, no visual change. */}
      <Helmet>
        <link rel="preload" as="image" href={heroImg} fetchpriority="high" />
      </Helmet>
      <div className="rr-premium-hero__overlay" />
      <img
        src={heroImg}
        alt={bgAlt}
        className="rr-premium-hero__bg-img"
        loading="eager"
      />
      {showCarAnimation && !vehicleImage && (
        <div className="rr-premium-hero__car-wheel">
          <picture>
            <source media="(max-width: 1024px)" srcSet="https://ridenrepair.com/images/car_mobile.webp" type="image/webp" />
            <img src="https://ridenrepair.com/images/car.webp" alt="Doorstep car and bike repair" width={1183} height={432} loading="eager" />
          </picture>
          <div className="rr-premium-hero__wheel rr-premium-hero__wheel--1">
            <img src="https://www.ridenrepair.com/images/wheel.webp" alt="" width={198} height={198} loading="eager" />
          </div>
          <div className="rr-premium-hero__wheel rr-premium-hero__wheel--2">
            <img src="https://www.ridenrepair.com/images/wheel.webp" alt="" width={198} height={198} loading="eager" />
          </div>
        </div>
      )}
      {vehicleImage && (
        <div className="rr-premium-hero__vehicle-overlay" aria-hidden="true">
          <img src={vehicleImage} alt={vehicleImageAlt} loading="eager" />
        </div>
      )}
      <div className="rr-premium-hero__content">
        {badge && <span className="rr-premium-hero__badge">{badge}</span>}
        <h1 className="rr-premium-hero__h1">
          {title}
          {priceTag && (
            <>
              <br />
              <span className="rr-premium-hero__price">{priceTag}</span>
            </>
          )}
        </h1>
        {subtitle && <p className="rr-premium-hero__subtitle">{subtitle}</p>}
        {ctas.length > 0 && (
          <div className="rr-premium-hero__ctas">
            {ctas.map((cta, i) => (
              <a
                key={i}
                href={cta.href}
                className={
                  cta.variant === 'secondary'
                    ? 'rr-cta-secondary'
                    : 'rr-cta-primary'
                }
              >
                {cta.label}
              </a>
            ))}
          </div>
        )}
        {trustItems.length > 0 && (
          <div className="rr-trust-bubbles">
            {trustItems.map((item, i) => (
              <div key={i} className="rr-trust-bubble">
                <span className="rr-trust-bubble__value">{item.value}</span>
                <span className="rr-trust-bubble__label">{item.label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default PremiumHero;
